import React, { useEffect, useRef, useMemo, useCallback, useState } from 'react'
import { select } from 'd3'
import * as d3 from "d3";
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { max } from 'd3-array';
import { json } from 'd3-fetch';
import useSWR from 'swr';
import { feature, mesh } from 'topojson-client';
import { geoIdentity, geoPath } from 'd3-geo';
import { transition } from 'd3-transition';
import classnames from 'classnames';
import Geocode from 'react-geocode';
import { scaleLinear, scaleSqrt, scaleSequential } from 'd3-scale';

import {
    D3_TRANSITION_DURATION,
    MAP_DIMENSIONS,
    MAP_META,
    MAP_TYPES,
    MAP_VIZS,
    STATE_CODES,
    STATE_NAMES,
    STATISTIC_CONFIGS,
    UNKNOWN_DISTRICT_KEY,
} from '../constants';

import {
    interpolateReds,
    interpolateBlues,
    interpolateGreens,
    interpolateGreys,
    interpolatePurples,
    interpolateOranges,
    interpolatePiYG,
} from 'd3-scale-chromatic';
import { circIn } from 'framer-motion';




function Map({ mapCode,
    cardData,
    isDistrictView,
    mapViz,
    data,
    regionHighlighted,
    setRegionHighlighted,
    statistic,
    mapStatistic,
    getMapStatistic,
    transformStatistic,
    districtData,
    noDistrictData, }) {

    const { t } = useTranslation();
    const svgRef = useRef(null);

    const mapMeta = MAP_META[mapCode];

    // const history = useNavigate();

    const { data: geoData } = useSWR(
        mapMeta?.geoDataFile,
        async (file) => {
            return await json(file);
        },
        { suspense: false, revalidateOnFocus: false }
    );





    const path = useMemo(() => {
        if (!geoData) return null;
        return geoPath(geoIdentity());
    }, [geoData]);






    const statisticConfig = STATISTIC_CONFIGS[statistic];

    const strokeColor = useCallback(
        (alpha) => (statisticConfig?.color || '#ffffff') + alpha,
        [statisticConfig]
    );


    useEffect(() => {
        const svg = select(svgRef.current);

        svg.attr('pointer-events', 'auto').on('click', () => {
            onceTouchedRegion.current = null;
            setRegionHighlighted({
                stateCode: mapCode,
                districtName: null,
            });
        });
    }, [mapCode, setRegionHighlighted]);



    const mapColor = useMemo(() => {
        if (mapStatistic === 'clients') {
            return 'rgba(0, 123, 255, 0.50)'
        } else if (mapStatistic === 'onboard') {
            return 'rgba(108, 117, 125, 0.50)'
        } else if (mapStatistic === 'active') {
            return 'rgba(40, 167, 69, 0.50)'
        } else if (mapStatistic === 'inactive') {
            return 'rgba(185, 28, 28,0.50)'
        }

    }, [mapStatistic])

    const features = useMemo(() => {
        if (!geoData) return null;

        const featuresWrap = !isDistrictView
            ? feature(geoData, geoData.objects.states).features
            : mapMeta.mapType === MAP_TYPES.COUNTRY && mapViz !== MAP_VIZS.CHOROPLETH
                ? [
                    ...feature(geoData, geoData.objects.states).features,
                    ...feature(geoData, geoData.objects.districts).features,
                ]
                : feature(geoData, geoData.objects.districts).features;
        // Add id to each feature
        return featuresWrap.map((feature) => {
            const district = feature.properties.district;
            const state = feature.properties.st_nm;
            const obj = Object.assign({}, feature);
            obj.id = `${mapCode}-${state}${district ? '-' + district : ''}`;
            //   //console.log(obj.id)
            return obj;
        });
    }, [geoData, mapCode, isDistrictView, mapViz, mapMeta, mapColor]);



    const onceTouchedRegion = useRef(null);

    const districtsSet = useMemo(() => {
        if (!geoData || !isDistrictView) return {};
        return feature(geoData, geoData.objects.districts).features.reduce(
            (stateCodes, feature) => {
                const stateCode = STATE_CODES[feature.properties.st_nm];
                if (!stateCodes[stateCode]) {
                    stateCodes[stateCode] = new Set();
                }
                stateCodes[stateCode].add(feature.properties.district);
                return stateCodes;
            },
            {}
        );
    }, [geoData, isDistrictView]);

    const statisticMax = useMemo(() => {
        const stateCodes = Object.keys(data).filter(
            (stateCode) =>
                stateCode !== 'TT' && Object.keys(MAP_META).includes(stateCode)
        );


        if (!isDistrictView) {
            return max(stateCodes, (stateCode) =>
                transformStatistic((data[stateCode]))
            );
        } else {
            const districtDataVal = stateCodes.reduce((res, stateCode) => {
                const districts = Object.keys(districtData[mapCode]?.district || []).filter(
                    (districtName) => { return districtName }

                );
                // //console.log(districts)
                res.push(
                    ...districts.map((districtName) =>
                        transformStatistic(
                            (districtData[stateCode]?.district[districtName])
                        )
                    )
                );
                return res;
            }, []);
            // //console.log(districtDataVal)
            // //console.log(max(districtDataVal))
            return max(districtDataVal);
        }

    })

    useEffect(() => {
        const svg = select(svgRef.current);

        svg.attr('pointer-events', 'auto').on('click', () => {
            onceTouchedRegion.current = null;
            setRegionHighlighted({
                stateCode: mapCode,
                districtName: null,
                value: cardData[mapStatistic === 'clients' ? 'client' : mapStatistic]
            });

        });
    }, [mapCode, setRegionHighlighted, mapStatistic]);




    const mapScale = useMemo(() => {
        return scaleSqrt([0, Math.max(1, statisticMax || 0)], [0, 40])
            .clamp(true)
            .nice(3);
    }, [mapViz, statistic, statisticMax]);





    useEffect(() => {
        if (!geoData) return;
        const svg = select(svgRef.current);
        const T = transition().duration(D3_TRANSITION_DURATION);

        let meshStates = [];
        let meshDistricts = [];

        if (mapMeta.mapType === MAP_TYPES.COUNTRY) {
            meshStates = [mesh(geoData, geoData.objects.states)];
            meshStates[0].id = `${mapCode}-states`;
        }
        if (
            mapMeta.mapType === MAP_TYPES.STATE ||
            (isDistrictView && mapViz === MAP_VIZS.CHOROPLETH)
        ) {
            meshDistricts = [mesh(geoData, geoData.objects.districts)];
            meshDistricts[0].id = `${mapCode}-districts`;
        }


        svg
            .select('.state-borders')
            .attr('fill', 'none')
            .attr('fill-opacity', 0.25)
            .attr('stroke-width', 1.5)
            .selectAll('path')
            .data(meshStates, (d) => d.id)
            .join(
                (enter) => enter.append('path').attr('d', path).attr('stroke', mapColor),
                (update) => update,
                (exit) => exit.transition(T).attr('stroke', mapColor).remove()
            )
            .transition(T)
            .attr('stroke', mapColor)
            ;


        svg
            .select('.district-borders')
            .attr('fill', 'none')
            .attr('stroke-width', 1.5)
            .attr('fill-opacity', 0.25)
            .selectAll('path')
            .data(meshDistricts, (d) => d.id)
            .join(
                (enter) =>
                    enter
                        .append('path')
                        .attr('d', path)
                        .attr('d', path)
                        .attr('stroke', mapColor),
                (update) => update,
                (exit) => exit.transition(T).attr('stroke', mapColor).remove()
            )
            .transition(T)
            .attr('stroke', mapColor);
    }, [
        geoData,
        mapMeta,
        mapCode,
        mapViz,
        isDistrictView,
        statistic,
        path,
        strokeColor,
        mapColor
    ]);




    const sortedFeatures = useMemo(() => {
        if (mapViz === MAP_VIZS.CHOROPLETH) {
            return [];
        } else {
            return (features || [])
                .map((feature) => {
                    // //console.log(data)
                    const stateCode = STATE_CODES[feature.properties.st_nm];
                    const districtName = feature.properties.district;
                    // //console.log(features)
                    let stateData;
                    if (!isDistrictView) {
                        stateData = data[stateCode];
                    } else {

                        stateData = districtData[mapCode]?.district[districtName]
                    }

                    feature.value = stateData;
                    // } 
                    // else {
                    //   const districtDataValue = stateData?.districts?.[districtName];
                    // //console.log(districtDataValue)

                    //   if (districtName) feature.value = getMapStatistic(districtData);
                    //   else
                    //     feature.value = getMapStatistic(
                    //       stateData?.districts?.[UNKNOWN_DISTRICT_KEY]
                    //     );
                    // }

                    return feature;
                })
                .filter((feature) => feature.value > 0)
                .sort((featureA, featureB) => featureB.value - featureB.value);
        }
    }, [mapViz, isDistrictView, getMapStatistic, features, data]);

    // //console.log(sortedFeatures)
    const populateTexts = useCallback(
        (regionSelection) => {
            regionSelection.select('title')
                .text((feature) => {
                    return `${feature.value} from ${feature.id?.split('-')[feature.id?.split('-')?.length - 1]} `
                })

            //   regionSelection.selectAll(".text")
            // .enter().append("text")
            // .attr("dx", 5)   //Move the text from the start angle of the arc
            // .attr("dy", 18) //Move the text down
            // .text(function(feature){return feature.value;});
        },
        [mapViz, data, getMapStatistic, statisticConfig,]
    );






    useEffect(() => {
        // const projection = d3.geoMercator()
        // .center([78.9629, 23.5937]) // Center of India
        // .scale(600)
        // .translate([MAP_DIMENSIONS[0] / 2, MAP_DIMENSIONS[1] / 2]);
        const svg = select(svgRef.current);
        const T = transition().duration(D3_TRANSITION_DURATION);

        const regionSelection = svg
            .select('.circles')
            .selectAll('circle')
            .data(
                // mapViz === MAP_VIZS.BUBBLE ? sortedFeatures : [],
                sortedFeatures,
                (feature) => feature.id
            )
            .join(
                (enter) =>
                    enter
                        .append('circle')
                        .attr(
                            'transform',
                            (feature) => {
                                // //console.log(feature)
                                return `translate(${path?.centroid(feature)})`
                            }
                        )
                        .attr('fill-opacity', 0.25)
                        .style('cursor', 'pointer')
                        .attr('pointer-events', 'all')
                        .call(enter =>
                            enter.append('title')
                        )
                ,
                (update) => update,
                (exit) => exit.call((exit) => exit.transition(T).attr('r', 0).remove())
            ).on('mouseenter', (event, feature) => {
                if (onceTouchedRegion.current) return;
                setRegionHighlighted({
                    stateCode: STATE_CODES[feature.properties.st_nm],
                    districtName: !isDistrictView
                        ? null
                        : feature.properties.district || UNKNOWN_DISTRICT_KEY,
                    value: feature.value
                });
            })
            .on('pointerdown', (event, feature) => {
                if (onceTouchedRegion.current === feature)
                    onceTouchedRegion.current = null;
                else onceTouchedRegion.current = feature;
                setRegionHighlighted({
                    stateCode: STATE_CODES[feature.properties.st_nm],
                    districtName: !isDistrictView
                        ? null
                        : feature.properties.district || UNKNOWN_DISTRICT_KEY,
                    value: feature.value
                });
            }).on('click', (event, feature) => {
                event.stopPropagation();

                if (onceTouchedRegion.current || mapMeta.mapType === MAP_TYPES.STATE)
                    return;
                // history.push(
                //   `/state/${STATE_CODES[feature.properties.st_nm]}${
                //     window.innerWidth < 769 ? '#MapExplorer' : ''
                //   }`
                // );
            })
            .call((sel) => {
                sel
                    .transition(T)
                    .attr('fill', mapColor)
                    .attr('stroke', mapColor)
                    .attr('r', (feature) => mapScale(feature.value));
            })





        // regionSelection.select('.fonts').selectAll('font').data(
        //   // mapViz === MAP_VIZS.BUBBLE ? sortedFeatures : [],
        //   sortedFeatures,
        //   (feature) => feature.id       
        // )
        // .join(
        //   (enter) =>
        //     enter
        //       .append('font')
        //       .attr(
        //         'transform',
        //         (feature) =>{
        //           // //console.log(feature)
        //         return `translate(${path?.centroid(feature)})`
        //         }
        //       )
        //       .attr('fill-opacity', 0.25)
        //       .style('cursor', 'pointer')
        //       .attr('pointer-events', 'all')
        //       ,
        //   (update) => update,
        //   (exit) => exit.call((exit) => exit.transition(T).attr('w', 30).remove())
        // ).attr('x',function(d,i){
        //   return i*100+45;
        // }).attr('y',105)
        // .attr('stroke','black')
        // .attr('font-size','20px').
        // attr('font-family','san-serif')
        // .text((feature)=>{
        //   return `${feature?.value} from ${feature?.id} `
        // }).call((sel) => {
        //   sel
        //     .transition(T)
        //     .attr('fill', mapColor)
        //     .attr('stroke',mapColor)
        //     .attr('w', (feature) =>mapScale(feature.value));
        // })


        // window?.requestIdleCallback(() => {
            populateTexts(regionSelection);
        // });








        //   "text-anchor": "middle",
        //   "font-size": function(d) { 
        //     return 15;
        //     },
        //   "dy": function(d) {
        //     return 15;
        //   }
        // });



    }, [data, sortedFeatures, mapColor, populateTexts, regionHighlighted?.stateCode,])




    useEffect(() => {
        const stateCode = regionHighlighted?.stateCode;
        const stateName = STATE_NAMES[stateCode];
        const district = regionHighlighted?.districtName;

        const svg = select(svgRef.current);
        svg
            .select('.circles')
            .selectAll('circle')
            .attr('fill-opacity', (d) => {
                const highlighted =
                    stateName === d.properties.st_nm &&
                    ((!district && stateCode !== mapCode) ||
                        district === d.properties?.district ||
                        !isDistrictView ||
                        (district === UNKNOWN_DISTRICT_KEY && !d.properties.district));
                return highlighted ? 1 : 0.25;
            });

    }, [
        geoData,
        data,
        mapCode,
        isDistrictView,
        mapViz,
        regionHighlighted?.stateCode,
        regionHighlighted?.districtName,
        statistic,
        mapStatistic,
        regionHighlighted?.value
    ]);




    return (
        <div className='map-main fadeInUp'>
            <svg id='chart' className='map'
                //   className={classnames({
                //     zone: !!statisticConfig?.mapConfig?.colorScale,
                //   })}
                viewBox={`0 0 ${MAP_DIMENSIONS[0]} ${MAP_DIMENSIONS[1]}`}
                // viewBox={`0 0 1500 1500`}
                preserveAspectRatio="xMidYMid meet"

                ref={svgRef}
            >


                <g className="regions" />
                <g className="state-borders" />
                <g className="district-borders" />
                <g className="circles" />
                <g className="fonts" />
            </svg>

        </div>
    )
}

export default Map
